import {makeAutoObservable} from "mobx";
import {ViewController} from "data/types/structure";
import {inject, injectable} from "inversify";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import type {IModalPayload, IModalsStore} from "data/stores/modals/modals.store";
import {GameSteps, ModalType} from "data/enums";
import {Bindings} from "data/constants/bindings";
import type {
	IDivisionSlot,
	ILightPredictionNew,
	ILightPredictionsStore,
} from "data/stores/light_predictions/light_predictions.store";
import type {IConferencesStore} from "data/stores/conferences/conferences.store";
import type {IStandingsStore, IUserStanding} from "data/stores/standings/standings.store";
import {eq, take} from "lodash";
import type {ISquadsStore} from "data/stores/squads/squads.store";
import {useNavigate} from "react-router-dom";
import type {IRoundsStore} from "data/stores/rounds/rounds.store";
import type {IOrderedStandingsStore} from "data/stores/ordered_standings/ordered_standings.store";
import {ConnextraType, createConnextraScript} from "data/utils/createConnextraScript";
import type {IUserStore} from "data/stores/user/user.store";
import type {IHeavyPredictionsStore} from "data/stores/heavy_predictions/heavy_predictions.store";

interface IProps {
	navigate: ReturnType<typeof useNavigate>;
}

export interface IModalPlayoffFinalController extends ViewController<IProps> {
	i18n: ILocalizationStore;

	get isOpen(): boolean;
	get modalContent(): IModalPayload | null;

	close: () => void;
	onFinalSave: () => void;
}

@injectable()
export class ModalPlayoffFinalController implements IModalPlayoffFinalController {
	private _navigate!: ReturnType<typeof useNavigate>;

	get isOpen(): boolean {
		return this._modalsStore.modal === ModalType.FINAL_SAVE;
	}

	get modalContent() {
		return this._modalsStore.modalContent;
	}

	get userNfcStandings() {
		return this.createStandingArr(
			this._standingsStore.userStandings,
			this._conferenceStore.nfcConferenceId
		);
	}

	get userAfcStandings() {
		return this.createStandingArr(
			this._standingsStore.userStandings,
			this._conferenceStore.afcConferenceId
		);
	}

	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.ModalsStore) private _userStore: IUserStore,
		@inject(Bindings.ModalsStore) private _modalsStore: IModalsStore,
		@inject(Bindings.RoundsStore) private _roundStore: IRoundsStore,
		@inject(Bindings.SquadsStore) private _squadsStore: ISquadsStore,
		@inject(Bindings.LightPredictionsStore) private _lpStore: ILightPredictionsStore,
		@inject(Bindings.LightPredictionsStore) private _hpStore: IHeavyPredictionsStore,
		@inject(Bindings.ConferencesStore) private _conferenceStore: IConferencesStore,
		@inject(Bindings.StandingsStore) private _standingsStore: IStandingsStore,
		@inject(Bindings.OrderedStandingsStore) private _orderStandingsStore: IOrderedStandingsStore
	) {
		makeAutoObservable(this);
	}

	private createStandingArr = (standings: IUserStanding[], conferenceId: number) => {
		const standingByRound = standings.filter((it) => {
			if (it.roundId === this._roundStore.actualStandingsRoundId) {
				const squad = this._squadsStore.getSquadById(it.squadId);
				return eq(squad?.conferenceId, conferenceId);
			}
			return false;
		});

		return take(this._orderStandingsStore.getSortedStandings(standingByRound, conferenceId), 7);
	};

	private transformToPayload = (nfcStandings: IUserStanding[], afcStandings: IUserStanding[]) => {
		const nfc = nfcStandings.map((el, index) => {
			return {
				type: GameSteps.NFC_WEST,
				squadId: el.squadId,
				position: index + 1,
			};
		}) as IDivisionSlot[];

		const afc = afcStandings.map((el, index) => {
			return {
				type: GameSteps.AFC_WEST,
				squadId: el.squadId,
				position: index + 1,
			};
		}) as IDivisionSlot[];

		return {
			nfc: nfc,
			afc: afc,
		};
	};

	close = () => {
		this._modalsStore.hideModal();
	};

	onFinalSave = () => {
		const predictions = this.transformToPayload(
			this.userNfcStandings,
			this.userAfcStandings
		) as ILightPredictionNew;

		this._hpStore.setIsPredictionsChanged(false);

		void this._lpStore.savePredictions(predictions);

		localStorage.setItem("final_save", "1");

		createConnextraScript(ConnextraType.HP_SUBMIT_PICKS, this._userStore.user?.id);

		this.close();

		this._navigate("/pick-by-seed");
	};

	init({navigate}: IProps) {
		this._navigate = navigate;
	}
}
